import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClone, faFileAlt, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import OrderName from "../../../OrderName";
import { Link } from "react-router-dom";
import { Badge, Dropdown } from "react-bootstrap";
import OrderStatusBadge from "../../../Ui/OrderStatusBadge";
import dayjs from "dayjs";
import IconButton from "../../../Buttons/IconButton";
import { faArrowsAlt } from "@fortawesome/free-solid-svg-icons";
import React, { useCallback, useState } from "react";
import { TApiOrder } from "../../../../api/endpoints/Order";
import { IFolder } from "../index";
import { useDispatch } from "react-redux";
import { duplicateOrder, moveOrderToFolder, removeOrder } from "../../../../store/action-creators/orders";
import ConfirmDialog from "../../../ConfrimDialog";
import { Nullable } from "../../../../../types/utils";

interface Props {
  order: TApiOrder;
  currentFolderId: Nullable<number>;
  availableFolders: Array<IFolder>;
}

const OrderListItem = (props: Props) => {
  const { order, availableFolders, currentFolderId } = props;
  const dispatch = useDispatch();
  const [duplicateWarningDialogOpen, setDuplicateWarningDialogOpen] = useState(false);

  const onMoveToFolder = useCallback((order: TApiOrder, targetFolderId: IFolder['id']) => () => {
    dispatch(moveOrderToFolder(order, currentFolderId, targetFolderId));
  }, [dispatch, currentFolderId]);

  const onRemove = useCallback((order: TApiOrder) => () => {
    dispatch(removeOrder(order.id));
  }, [dispatch]);

  const onDuplicateConfirmation = useCallback((order: TApiOrder) => () => {
    // @ts-ignore
    dispatch(duplicateOrder(order)).then(() => {
      setDuplicateWarningDialogOpen(false);
    });
  }, [dispatch]);

  const onDuplicate = useCallback((order: TApiOrder) => () => {
    if (order.has_legs) {
      setDuplicateWarningDialogOpen(true);
    } else {
      dispatch(duplicateOrder(order));
    }
  }, [dispatch]);

  return (
    <tr key={`order-${order.id}`}>
      <td className="order-type"><FontAwesomeIcon icon={faFileAlt} /></td>

      <td className="order-name">
        <OrderName order={order} />
      </td>

      <td className="order-reference">
        <Link to={'/orders/' + order.id}>
          ref: {order.id}
        </Link>
      </td>

      <td className="order-category">
        <Badge variant="info">{order.category}</Badge>
      </td>

      <td className="order-status">
        <OrderStatusBadge status={order.status} />
      </td>

      <td>{order.customer}</td>

      <td className="time">{dayjs(order.updated).fromNow()}</td>

      <td className="order-actions">
        <Dropdown>
          <Dropdown.Toggle
            as={IconButton}
            icon={faArrowsAlt}
            title="Move to folder"
            variant="primary"
            id="dropdown-custom-components"
          />

          <Dropdown.Menu>
            {
              availableFolders.map(folder =>
                <Dropdown.Item
                  key={folder.id ?? 'root'}
                  as="button"
                  onClick={onMoveToFolder(order, folder.id)}
                >
                  {folder.name}
                </Dropdown.Item>
              )
            }
          </Dropdown.Menu>
        </Dropdown>

        <IconButton icon={faClone} onClick={onDuplicate(order)} title="Duplicate order" />
        <IconButton icon={faTrashAlt} onClick={onRemove(order)} variant="danger" title="Remove order" />
      </td>

      <ConfirmDialog
        open={duplicateWarningDialogOpen}
        onClose={() => setDuplicateWarningDialogOpen(false)}
        onConfirm={onDuplicateConfirmation(order)}
        cancelText="Cancel"
        confirmText="Proceed"
        message="Please be aware that the quote you want to duplicate contains products using Legs (ENS extended products used to have Leg 1 and Leg 2 time steps), which are no longer available in the new 48r1 cycle. Previous quotes with Legs will be automatically updated to match the new 48r1 cycle requirements."
      />
    </tr>
  )
}

export default OrderListItem;
